import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    ArrowUpRight,
    PlaneTakeoff,
    Route,
    Clock,
    MapPin,
    Users,
} from "lucide-react";
import { useData } from "../../context/DataContext";
import { formatCurrency } from "../../lib/format";
import SEO from "../../components/SEO";

const CATS = [
    { v: "all", l: "All" },
    { v: "airport-hotel", l: "Airport", icon: PlaneTakeoff },
    { v: "inter-city", l: "Inter-city", icon: Route },
];

const fmtDuration = (min) => {
    if (!min) return "—";
    const h = Math.floor(min / 60);
    const m = min % 60;
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} h`;
    return `${h} h ${m}`;
};

const PreviewTransfers = () => {
    const { transfers } = useData();
    const { t } = useTranslation();
    const [cat, setCat] = useState("all");

    const visible = useMemo(() => {
        let list = (transfers || []).filter((tr) => tr.active !== false);
        if (cat !== "all") list = list.filter((tr) => tr.category === cat);
        list.sort((a, b) => a.sedanPrice - b.sedanPrice);
        return list;
    }, [transfers, cat]);

    const serviceJsonLd = {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Kerala Airport Transfers",
        provider: {
            "@type": "TravelAgency",
            name: "SouthTrip.in by LuxSaCabs",
            url: "https://southtrip.in",
        },
        areaServed: { "@type": "Place", name: "Kerala, India" },
        serviceType: "Airport taxi transfer service",
    };

    return (
        <div data-testid="preview-transfers">
            <SEO
                title={t("seo.transfersTitle")}
                description={t("seo.transfersDescription")}
                path="/airport-transfers"
                jsonLd={serviceJsonLd}
                keywords="cochin airport taxi, trivandrum airport transfer, calicut airport taxi, cok airport pickup, trv airport taxi, ccj airport taxi, kerala airport transfer"
            />
            <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 pb-8 sm:pb-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-end">
                    <div className="md:col-span-8">
                        <div className="hz-pill">
                            <PlaneTakeoff className="w-3 h-3 mr-1" /> Fixed-price transfers · {transfers.length} routes
                        </div>
                        <h1 className="font-hz-display text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] mt-4 sm:mt-5">
                            {t("seo.transfersH1")}
                        </h1>
                    </div>
                    <div className="md:col-span-4">
                        <p className="text-[var(--ink-soft)] text-sm sm:text-base leading-relaxed">
                            Point-to-point rides from <b>Cochin (COK)</b>, <b>Trivandrum (TRV)</b> and{" "}
                            <b>Calicut (CCJ)</b> airports — fixed fares, zero surge, flight-tracking
                            drivers. Pay direct on arrival.
                        </p>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 sticky top-[68px] sm:top-[72px] z-20 bg-[var(--bg)] border-y border-[var(--border)]">
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap text-sm">
                    <span className="text-[var(--ink-soft)] text-[10px] sm:text-xs uppercase tracking-widest">Category</span>
                    {CATS.map((c) => (
                        <button
                            key={c.v}
                            onClick={() => setCat(c.v)}
                            className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition inline-flex items-center gap-1 ${
                                cat === c.v
                                    ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                                    : "border-[var(--border)] text-[var(--ink-soft)] hover:border-[var(--ink)]"
                            }`}
                        >
                            {c.icon && <c.icon className="w-3 h-3" />} {c.l}
                        </button>
                    ))}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                    {visible.map((tr, i) => {
                        const isFeature = i === 0;
                        return (
                            <Link
                                to={`/airport-transfers/book/${tr.id}`}
                                key={tr.id}
                                className={`hz-card group overflow-hidden flex flex-col ${isFeature ? "md:col-span-2 md:row-span-2" : ""}`}
                                data-testid={`preview-transfer-${tr.id}`}
                            >
                                <div className={`relative overflow-hidden bg-[var(--bg-surface)] ${isFeature ? "aspect-[16/10]" : "aspect-[4/3]"}`}>
                                    <img src={tr.image} alt={tr.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" loading="lazy" />
                                    <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                                        <span className="hz-pill bg-white/90">
                                            {tr.category === "airport-hotel" ? <PlaneTakeoff className="w-3 h-3" /> : <Route className="w-3 h-3" />}
                                            {tr.category === "airport-hotel" ? "Airport" : "Inter-city"}
                                        </span>
                                        <span className="hz-pill" style={{ background: "var(--ink)", color: "#fff", border: "none" }}>
                                            <Clock className="w-3 h-3" /> {fmtDuration(tr.durationMin)}
                                        </span>
                                    </div>
                                </div>
                                <div className={`${isFeature ? "p-6 lg:p-8" : "p-5 sm:p-6"} flex-1 flex flex-col`}>
                                    <h2 className={`font-hz-display leading-tight tracking-tight ${isFeature ? "text-2xl sm:text-3xl md:text-4xl" : "text-lg sm:text-xl"}`}>
                                        {tr.title}
                                    </h2>
                                    <div className="text-xs text-[var(--ink-softer)] mt-2 flex items-center gap-3 flex-wrap">
                                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {tr.distanceKm} km</span>
                                        {isFeature && <span className="line-clamp-2 text-[var(--ink-soft)]">{tr.description}</span>}
                                    </div>
                                    <div className={`mt-auto pt-5 grid ${isFeature ? "grid-cols-3" : "grid-cols-2"} gap-2`}>
                                        <PriceChip label="Sedan" value={tr.sedanPrice} big={isFeature} />
                                        <PriceChip label="SUV" value={tr.suvPrice} big={isFeature} />
                                        {isFeature && <PriceChip label="Tempo" value={tr.tempoPrice} big />}
                                    </div>
                                    <div className="mt-5 flex items-center justify-between">
                                        <div className="text-[10px] text-[var(--ink-softer)] uppercase tracking-widest">From</div>
                                        <span className="hz-btn-primary text-xs py-2 px-4">
                                            Book <ArrowUpRight className="w-3 h-3" />
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* ===== SEO LONG-FORM CONTENT ===== */}
            <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 lg:py-24" data-testid="transfers-seo-content">
                <h2 className="font-hz-display text-3xl md:text-4xl tracking-tight">Kerala airport transfers from Cochin, Trivandrum and Calicut</h2>
                <p className="text-[var(--ink-soft)] mt-4 leading-relaxed">
                    SouthTrip airport transfers cover all three Kerala international airports — Cochin International Airport (COK), Trivandrum International Airport (TRV) and Calicut International Airport (CCJ). Whether your flight lands at 02:00 from Dubai or at 18:00 from Doha, our drivers track every flight in real-time and meet you in the arrivals hall with your name on a placard. There is no surge pricing and no waiting fee for the first 60 minutes.
                </p>
                <h3 className="font-hz-display text-2xl mt-10">Cochin Airport (COK) taxi to Munnar, Alleppey, Fort Kochi & beyond</h3>
                <p className="text-[var(--ink-soft)] mt-3 leading-relaxed">
                    Cochin airport taxi service is our most-booked transfer. Popular routes include Cochin Airport to Fort Kochi (45 km), Cochin Airport to Munnar (110 km, 4h drive through the Western Ghats), Cochin Airport to Alleppey houseboat jetty (90 km) and Cochin Airport to Thekkady (165 km). Each transfer is fixed-price for sedan, SUV (Innova Crysta) or 12-seater tempo, with toll already included.
                </p>
                <h3 className="font-hz-display text-2xl mt-10">Trivandrum Airport (TRV) transfers to Kovalam, Varkala and Kanyakumari</h3>
                <p className="text-[var(--ink-soft)] mt-3 leading-relaxed">
                    Trivandrum airport transfer is the gateway for southern Kerala beach holidays. Standard fares cover Trivandrum Airport to Kovalam beach (15 km, 25 min), Trivandrum Airport to Varkala cliff (45 km), Trivandrum to Kanyakumari (90 km) and the full Kerala south circuit. Inter-state pickups to Tamil Nadu via Kanyakumari and Madurai are available with the right permits.
                </p>
                <h3 className="font-hz-display text-2xl mt-10">Calicut Airport (CCJ) transfers to Wayanad and northern Kerala</h3>
                <p className="text-[var(--ink-soft)] mt-3 leading-relaxed">
                    Calicut airport taxi service is the entry point for Wayanad jungle escapes and the cultural cities of north Kerala. Most-booked routes are Calicut Airport to Wayanad (95 km, 3h scenic ghat drive), Calicut Airport to Bekal Fort (135 km), Calicut to Kannur and Calicut to Mysore via the Bandipur national park.
                </p>
                <h3 className="font-hz-display text-2xl mt-10">Why book your Kerala airport taxi with SouthTrip</h3>
                <ul className="text-[var(--ink-soft)] mt-3 space-y-2 list-disc pl-6 leading-relaxed">
                    <li>Fixed price across all 3 Kerala airports — no surge, no fuel surcharge.</li>
                    <li>Live flight tracking + 60 min free wait time inside arrivals.</li>
                    <li>Verified Kerala-trained chauffeurs · English / Malayalam / Hindi / Arabic on request.</li>
                    <li>Sedan, SUV (Innova Crysta) and 12-seat tempo for solo travellers, families or groups.</li>
                    <li>Pay direct to driver — cash, UPI, card. No prepayment.</li>
                </ul>
                <p className="text-[var(--ink-soft)] mt-6 leading-relaxed">
                    Combine your transfer with a Kerala <Link to="/cab-booking" className="hz-link">private-driver cab booking</Link>, an inclusive multi-day <Link to="/tour-packages" className="hz-link">tour package</Link>, or browse the most-booked <Link to="/destinations" className="hz-link">Kerala destinations</Link>.
                </p>
            </section>
        </div>
    );
};

const PriceChip = ({ label, value, big }) => (
    <div className={`rounded-xl border border-[var(--border)] text-center ${big ? "p-3" : "p-2"}`}>
        <div className="text-[10px] text-[var(--ink-softer)] uppercase tracking-widest">{label}</div>
        <div className={`font-hz-display leading-none mt-1 ${big ? "text-2xl" : "text-lg"}`}>
            {value ? `₹${value.toLocaleString("en-IN")}` : "—"}
        </div>
    </div>
);

export default PreviewTransfers;
